import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Folder, ListFilter, Menu, Tag, X } from 'lucide-react';
import { ProjectFilter, UserFilter } from './types/dashboardTypes';
import DashboardUserFilterDropdown from './DashboardUserFilterDropdown';
import DashboardTagFilterDropdown from './DashboardTagFilterDropdown';
import { cn, formatUserDisplayName } from '../../lib/utils';
import { FilterButton, Dropdown, DropdownContent, DropdownHeader, DropdownItem } from '../ui';
import UserGreeting from './UserGreeting';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';

interface DashboardHeaderProps {
  isMobile: boolean;
  projectFilters: ProjectFilter[];
  userFilters: UserFilter[];
  isFilterDropdownOpen: boolean;
  setIsFilterDropdownOpen: (open: boolean) => void;
  toggleProjectFilter: (projectId: string, isExclusive?: boolean) => void;
  toggleUserFilter: (userId: string, isExclusive?: boolean) => void;
  setIsDrawerOpen: (open: boolean) => void;
  // Tag filter properties
  availableTags?: string[];
  selectedTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  // Active user IDs for filter state
  activeUserIds?: string[];
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  isMobile,
  projectFilters,
  userFilters,
  isFilterDropdownOpen,
  setIsFilterDropdownOpen,
  toggleProjectFilter,
  toggleUserFilter,
  setIsDrawerOpen,
  availableTags = [],
  selectedTags = [],
  onTagsChange = () => {},
  activeUserIds = [],
}) => {
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();
  const [isUserFilterOpen, setIsUserFilterOpen] = useState(false);
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
  const filtersWithTasks = projectFilters ? projectFilters.filter(filter => filter.hasTasks) : [];

  // The Dropdown component handles outside clicks automatically

  // Ensure only one dropdown is open at a time
  useEffect(() => {
    // Close other dropdowns when project filter dropdown opens
    if (isFilterDropdownOpen) {
      if (isUserFilterOpen) setIsUserFilterOpen(false);
      if (isTagFilterOpen) setIsTagFilterOpen(false);
    }
  }, [isFilterDropdownOpen]);

  useEffect(() => {
    // Close other dropdowns when user filter dropdown opens
    if (isUserFilterOpen) {
      if (isFilterDropdownOpen) setIsFilterDropdownOpen(false);
      if (isTagFilterOpen) setIsTagFilterOpen(false);
    }
  }, [isUserFilterOpen]);

  useEffect(() => {
    // Close other dropdowns when tag filter dropdown opens
    if (isTagFilterOpen) {
      if (isFilterDropdownOpen) setIsFilterDropdownOpen(false);
      if (isUserFilterOpen) setIsUserFilterOpen(false);
    }
  }, [isTagFilterOpen]);

  // Handler for setting user filter dropdown state
  const setUserFilterDropdownState = (newState?: boolean) => {
    // If explicit state is provided, use it, otherwise toggle
    const nextState = newState !== undefined ? newState : !isUserFilterOpen;
    setIsUserFilterOpen(nextState);
    // If we're opening user filter, close other filters
    if (nextState) {
      if (isFilterDropdownOpen) setIsFilterDropdownOpen(false);
      if (isTagFilterOpen) setIsTagFilterOpen(false);
    }
  };

  // Handler for setting tag filter dropdown state
  const setTagFilterDropdownState = (newState?: boolean) => {
    // If explicit state is provided, use it, otherwise toggle
    const nextState = newState !== undefined ? newState : !isTagFilterOpen;
    setIsTagFilterOpen(nextState);
    // If we're opening tag filter, close other filters
    if (nextState) {
      if (isFilterDropdownOpen) setIsFilterDropdownOpen(false);
      if (isUserFilterOpen) setIsUserFilterOpen(false);
    }
  };

  return (
    <div className={`${isMobile ? 'mt-14 py-4' : 'py-4'} px-6 w-full`}>
      <div className="w-full flex justify-between items-start flex-wrap">
        {/* Personalized greeting */}
        <div className="flex items-center">{!isMobile && <UserGreeting />}</div>

        {/* Desktop filters section */}
        {!isMobile && (
          <div className="flex flex-wrap gap-2 justify-end">
            {/* Tag filter dropdown for desktop - moved to be first */}
            {availableTags && availableTags.length > 0 && (
              <DashboardTagFilterDropdown
                availableTags={availableTags}
                selectedTags={selectedTags}
                onTagsChange={onTagsChange}
                isOpen={isTagFilterOpen}
                setDropdownState={setTagFilterDropdownState}
                isMobile={false}
              />
            )}

            {/* Project filter dropdown for desktop */}
            {filtersWithTasks && filtersWithTasks.length > 0 && (
              <Dropdown
                trigger={
                  <FilterButton
                    isActive={isFilterDropdownOpen}
                    hasFilters={projectFilters && projectFilters.filter(f => f.active).length > 0}
                    onClick={() => {}}
                    onClear={e => {
                      e.stopPropagation();
                      // Clear all project filters by calling toggleProjectFilter for each active filter
                      projectFilters?.forEach(filter => {
                        if (filter.active) {
                          toggleProjectFilter(filter.id, true);
                        }
                      });
                      // Close the dropdown after clearing filters
                      setIsFilterDropdownOpen(false);
                    }}
                    label={
                      projectFilters && projectFilters.filter(f => f.active).length === 1
                        ? projectFilters.find(f => f.active)?.name || 'Projects'
                        : projectFilters && projectFilters.filter(f => f.active).length > 1
                          ? `Multiple Projects (${projectFilters.filter(f => f.active).length})`
                          : 'Projects'
                    }
                    icon={<Folder size={16} className="text-blue-500" />}
                    isMobile={isMobile}
                    className=""
                  />
                }
                open={isFilterDropdownOpen}
                onOpenChange={setIsFilterDropdownOpen}
                position="bottom-left"
                width={288}
              >
                <DropdownContent>
                  <DropdownHeader>Projects with Deadlines</DropdownHeader>
                  {filtersWithTasks.map(filter => (
                    <DropdownItem
                      key={filter.id}
                      leftIcon={<Folder size={16} className="text-blue-500" />}
                      rightIcon={
                        filter.active ? (
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-500 border border-green-500">
                            <Check size={14} className="text-white" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border border-gray-300" />
                        )
                      }
                      onClick={e => {
                        toggleProjectFilter(
                          filter.id,
                          !e.ctrlKey && !e.metaKey // Enable multi-select with Ctrl/Cmd key
                        );

                        // Close dropdown if this was a simple click (no Ctrl/Cmd)
                        if (!e.ctrlKey && !e.metaKey) {
                          setIsFilterDropdownOpen(false);
                        }
                      }}
                    >
                      <span className="truncate">{filter.name}</span>
                    </DropdownItem>
                  ))}
                </DropdownContent>
              </Dropdown>
            )}

            {/* User filter dropdown for desktop */}
            {userFilters && userFilters.length > 0 && (
              <DashboardUserFilterDropdown
                userFilters={userFilters}
                isOpen={isUserFilterOpen}
                setDropdownState={setUserFilterDropdownState}
                onSelect={toggleUserFilter}
                isMobile={false}
                activeFilters={{ userIds: activeUserIds }}
              />
            )}
          </div>
        )}
      </div>

      {/* Mobile controls - rendered outside of the header */}
      {isMobile && (
        <>
          {/* Burger menu with fixed position at the top left */}
          <div
            className="fixed left-4 z-[40]"
            style={{ top: isNativeApp ? `calc(4px + ${statusBarHeight}px)` : '4px' }}
          >
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors bg-white/80 shadow-sm"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>

          {/* Filter menu with fixed position at the top right */}
          {((filtersWithTasks && filtersWithTasks.length > 0) ||
            (userFilters && userFilters.length > 0)) && (
            <>
              <div
                className="fixed right-4 z-40"
                style={{ top: isNativeApp ? `calc(4px + ${statusBarHeight}px)` : '4px' }}
              >
                <button
                  onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                  className={cn(
                    'p-2 text-gray-600 hover:text-gray-900 rounded-lg transition-colors bg-white/80 shadow-sm',
                    projectFilters.some(f => f.active) || userFilters.some(f => f.active)
                      ? 'bg-blue-50/90 text-blue-700 shadow-md ring-1 ring-blue-300'
                      : 'hover:bg-gray-100'
                  )}
                  aria-label="Filter dashboard"
                >
                  <ListFilter size={24} />
                  {((projectFilters && projectFilters.some(f => f.active)) ||
                    (userFilters && userFilters.some(f => f.active)) ||
                    (selectedTags && selectedTags.length > 0)) && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] font-bold px-1">
                      {(projectFilters ? projectFilters.filter(f => f.active).length : 0) +
                        (userFilters ? userFilters.filter(f => f.active).length : 0) +
                        (selectedTags && selectedTags.length > 0 ? 1 : 0)}
                    </span>
                  )}
                </button>
              </div>

              {/* We're now letting the Dropdown component handle the mobile modal UI */}
              {isFilterDropdownOpen &&
                createPortal(
                  <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center">
                    <div className="w-full h-full bg-white flex flex-col overflow-hidden">
                      {/* Modal header */}
                      <div className="px-4 py-3 border-b border-gray-800 bg-gray-900 flex justify-between items-center">
                        <h2 className="text-lg font-medium text-white">Filters</h2>
                        <button
                          onClick={() => setIsFilterDropdownOpen(false)}
                          className="text-white hover:text-white/80 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>

                      {/* Scrollable content */}
                      <div className="flex-1 overflow-y-auto">
                        {/* Projects section for mobile */}
                        {filtersWithTasks && filtersWithTasks.length > 0 && (
                          <>
                            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                              <h3 className="font-medium text-gray-700">Projects with Deadlines</h3>
                              {projectFilters && projectFilters.some(f => f.active) && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    // Clear all project filters
                                    projectFilters?.forEach(filter => {
                                      if (filter.active) {
                                        toggleProjectFilter(filter.id, true);
                                      }
                                    });
                                    // Close the dropdown after clearing
                                    setTimeout(() => {
                                      setIsFilterDropdownOpen(false);
                                    }, 150);
                                  }}
                                  className="text-blue-400 hover:text-blue-600 transition-colors"
                                  aria-label="Clear all project filters"
                                  title="Clear all project filters"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                            {filtersWithTasks.map(filter => (
                              <div
                                key={filter.id}
                                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                onClick={() => {
                                  toggleProjectFilter(filter.id, true); // Always exclusive on mobile

                                  // Close the dropdown after a short delay to show the selection
                                  setTimeout(() => {
                                    setIsFilterDropdownOpen(false);
                                  }, 150);
                                }}
                              >
                                <div className="flex-1 flex items-center gap-2 min-w-0">
                                  <Folder size={16} className="text-blue-500 flex-shrink-0" />
                                  <span className="pr-5 truncate">{filter.name}</span>
                                </div>
                                <div
                                  className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                                    filter.active
                                      ? 'bg-green-500 border-green-500'
                                      : 'border-gray-300'
                                  }`}
                                >
                                  {filter.active && <Check size={14} className="text-white" />}
                                </div>
                              </div>
                            ))}
                          </>
                        )}

                        {/* Users section for mobile */}
                        {userFilters && userFilters.length > 0 && (
                          <>
                            <div className="px-3 py-2 border-t border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                              <h3 className="font-medium text-gray-700">People</h3>
                              {userFilters && userFilters.some(f => f.active) && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    // Clear all user filters
                                    userFilters?.forEach(filter => {
                                      if (filter.active) {
                                        toggleUserFilter(filter.id, true);
                                      }
                                    });
                                    // Close the dropdown after clearing
                                    setTimeout(() => {
                                      setIsFilterDropdownOpen(false);
                                    }, 150);
                                  }}
                                  className="text-blue-400 hover:text-blue-600 transition-colors"
                                  aria-label="Clear all user filters"
                                  title="Clear all user filters"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                            {userFilters &&
                              userFilters.map(filter => (
                                <div
                                  key={filter.id}
                                  className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  onClick={() => {
                                    toggleUserFilter(filter.id, true); // Always exclusive on mobile

                                    // Close the dropdown after a short delay to show the selection
                                    setTimeout(() => {
                                      setIsFilterDropdownOpen(false);
                                    }, 150);
                                  }}
                                >
                                  <div className="flex-1 flex items-center gap-2 min-w-0">
                                    {filter.avatar_url ? (
                                      <img
                                        src={filter.avatar_url}
                                        alt={filter.name}
                                        className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                                        {formatUserDisplayName(filter.name)?.charAt(0) || '?'}
                                      </div>
                                    )}
                                    <span className="pr-5 truncate">
                                      {formatUserDisplayName(filter.name)}
                                    </span>
                                  </div>
                                  <div
                                    className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                                      filter.active
                                        ? 'bg-green-500 border-green-500'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {filter.active && <Check size={14} className="text-white" />}
                                  </div>
                                </div>
                              ))}
                          </>
                        )}

                        {/* Tags section for mobile */}
                        {availableTags && availableTags.length > 0 && (
                          <>
                            <div className="px-3 py-2 border-t border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                              <h3 className="font-medium text-gray-700">Tags</h3>
                              {selectedTags && selectedTags.length > 0 && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    // Clear all tag filters
                                    onTagsChange([]);
                                    // Close the dropdown after clearing
                                    setTimeout(() => {
                                      setIsFilterDropdownOpen(false);
                                    }, 150);
                                  }}
                                  className="text-blue-400 hover:text-blue-600 transition-colors"
                                  aria-label="Clear all tag filters"
                                  title="Clear all tag filters"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                            {availableTags &&
                              availableTags.map(tag => (
                                <div
                                  key={tag}
                                  className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  onClick={() => {
                                    // Toggle tag selection
                                    if (selectedTags && selectedTags.includes(tag)) {
                                      onTagsChange(selectedTags.filter(t => t !== tag));
                                    } else {
                                      onTagsChange([...(selectedTags || []), tag]);
                                    }

                                    // Don't close dropdown on tag selection to allow multiple selections
                                  }}
                                >
                                  <div className="flex-1 flex items-center gap-2 min-w-0">
                                    <Tag size={16} className="text-blue-500 flex-shrink-0" />
                                    <span className="pr-5 truncate">{tag}</span>
                                  </div>
                                  <div
                                    className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                                      selectedTags && selectedTags.includes(tag)
                                        ? 'bg-green-500 border-green-500'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {selectedTags && selectedTags.includes(tag) && (
                                      <Check size={14} className="text-white" />
                                    )}
                                  </div>
                                </div>
                              ))}
                          </>
                        )}
                      </div>

                      {/* Done button */}
                      <div className="p-4 mt-auto">
                        <button
                          onClick={() => setIsFilterDropdownOpen(false)}
                          className="w-full py-2.5 px-4 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardHeader;
